import type { TariffVersion } from '@/types/billing';

// Canonical default rounding values (single source of truth on the web side;
// mirrors the backend models.DefaultRounding* constants: OFF / step 5 Rappen).
export const ROUNDING_DEFAULTS = {
  parentsEnabled: false,
  parentsAmount: 5,
  subsidyEnabled: false,
  subsidyAmount: 5,
};

export interface InheritedRounding {
  parentsEnabled: boolean;
  parentsAmount: number;
  subsidyEnabled: boolean;
  subsidyAmount: number;
}

// Structural accessor for the fields we read off a tariff version. Declared
// locally (with both snake_case and camelCase variants) so this helper does not
// depend on the @/types/billing TariffVersion type resolving its
// billing_TariffVersionResponse base (a pre-existing baseline import issue).
interface RoundingVersionFields {
  groupType?: string;
  group_type?: string;
  status?: string;
  createdAt?: string;
  created_at?: string;
  roundingParentsEnabled?: boolean;
  rounding_parents_enabled?: boolean;
  roundingParentsAmount?: number;
  rounding_parents_amount?: number;
  roundingSubsidyEnabled?: boolean;
  rounding_subsidy_enabled?: boolean;
  roundingSubsidyAmount?: number;
  rounding_subsidy_amount?: number;
}

/**
 * resolveInheritedRounding returns the rounding settings inherited by a NEW
 * tariff version: those of the most-recent ACTIVE version of the same group
 * type, or the canonical defaults (OFF / step 5) when none exists.
 *
 * Used as the form prefill; the server-side service applies the same rule as the
 * authoritative fallback.
 */
export function resolveInheritedRounding(
  versions: TariffVersion[] | undefined,
  groupType: string | undefined,
): InheritedRounding {
  const fallback: InheritedRounding = { ...ROUNDING_DEFAULTS };
  if (!versions || versions.length === 0 || !groupType) {
    return fallback;
  }

  const list = versions as unknown as RoundingVersionFields[];
  const candidates = list
    .filter(
      (v) => (v.groupType || v.group_type) === groupType && v.status === 'ACTIVE',
    )
    .sort((a, b) => {
      const ad = a.createdAt || a.created_at || '';
      const bd = b.createdAt || b.created_at || '';
      return bd.localeCompare(ad); // most-recent (created_at DESC) first
    });

  const top = candidates[0];
  if (!top) {
    return fallback;
  }

  return extractRounding(top);
}

/**
 * extractRounding reads a single version's rounding settings (tolerating both
 * snake_case and camelCase), falling back to the canonical defaults for any
 * missing field. Amounts below 1 are coerced to the default step. Used to
 * prefill the form on copy/edit.
 */
export function extractRounding(
  version: TariffVersion | undefined,
): InheritedRounding {
  const fallback: InheritedRounding = { ...ROUNDING_DEFAULTS };
  if (!version) {
    return fallback;
  }
  const v = version as unknown as RoundingVersionFields;
  const parentsEnabled =
    v.roundingParentsEnabled ?? v.rounding_parents_enabled ?? fallback.parentsEnabled;
  const subsidyEnabled =
    v.roundingSubsidyEnabled ?? v.rounding_subsidy_enabled ?? fallback.subsidyEnabled;
  const parentsAmountRaw =
    v.roundingParentsAmount ?? v.rounding_parents_amount ?? fallback.parentsAmount;
  const subsidyAmountRaw =
    v.roundingSubsidyAmount ?? v.rounding_subsidy_amount ?? fallback.subsidyAmount;

  return {
    parentsEnabled,
    parentsAmount: parentsAmountRaw >= 1 ? parentsAmountRaw : fallback.parentsAmount,
    subsidyEnabled,
    subsidyAmount: subsidyAmountRaw >= 1 ? subsidyAmountRaw : fallback.subsidyAmount,
  };
}
